import { Box, Button, IconButton, Tab, Tabs, TextField, Typography } from "@mui/material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import KeyboardDoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import { useEffect, useRef, useState } from "react";
import { api } from "../../../api/API";
import { Attachment, Comment, emptyRepeat, Repeat, Task, TaskInput, TaskLink } from "../../../api/Tasks";
import { useCurrentUser } from "../../../misc/CurrentUser";
import { FieldRow } from "../../../misc/FieldRow";
import { Hint } from "../../../misc/Hint";
import { HtmlEditor } from "../../../misc/HtmlEditor";
import { useSnackAction } from "../../../misc/SnackAction";
import { useSyncedRef } from "../../../misc/SyncedRef";
import { useUIFlags } from "../../../misc/UIFlags";
import { useAutoSave } from "../../../misc/useAutoSave";
import { TinyButton } from "../../../misc/TinyButton";
import { isPersonalTeam, useWorkspace } from "../../../misc/Workspace";
import { AssigneePicker } from "./AssigneePicker";
import { CompletedDateButton } from "./CompletedDateButton";
import { DueDateButton } from "./DueDateButton";
import { Crumb, TaskCrumbs, TrailStep } from "./TaskCrumbs";
import { TaskAttachments } from "./TaskAttachments";
import { TaskChecklist } from "./TaskChecklist";
import { ChecklistProgress } from "./ChecklistProgress";
import { TaskComments } from "./TaskComments";
import { TaskGantt } from "./TaskGantt";
import { TaskLinks } from "./TaskLinks";
import { panelFontSize, panelGutter } from "./PanelSection";
import { ProjectRef, TaskProject } from "./TaskProject";
import { taskPermalink } from "../TaskPicker";
import { TaskPanelMenu } from "./TaskPanelMenu";
import { useMoveTask } from "./useMoveTask";
import Checklist from "@mui/icons-material/Checklist";
import Block from "@mui/icons-material/Block";
import ArrowForward from "@mui/icons-material/ArrowForward";
import Timeline from "@mui/icons-material/Timeline";
import {Check} from "@mui/icons-material";

export function TaskPanel(props: {
    value: Partial<Task>;
    onClose: () => void;
    onSaved: (task: Task) => void;

    // onOpen walks into one of the linked tasks; crumbs is the trail walked
    // so far (see TaskPanelStack)
    onOpen: (crumb: Crumb) => void;
    crumbs: TrailStep[];

    // onOpenProject leaves the task for what it's filed under, which the panel slot
    // swaps in behind a step back to here
    onOpenProject?: (ref: { task: number; project: number; goal: number }) => void;

    // onMoved runs after the task was put on another team, which changes what the
    // panel can offer, so it's read again
    onMoved: () => void;
}) {
    const [title, setTitle] = useState(props.value.title || "");
    const [description, setDescription] = useState(props.value.description || "");
    const [dueDate, setDueDate] = useState(props.value.dueDate || "");
    const [dueTime, setDueTime] = useState(props.value.dueTime || "");
    const [repeat, setRepeat] = useState<Repeat>(props.value.repeat || emptyRepeat);
    const [blockedBy, setBlockedBy] = useState<TaskLink[]>(props.value.blockedBy || []);
    const [followedBy, setFollowedBy] = useState<TaskLink[]>(props.value.followedBy || []);
    const [checklist, setChecklist] = useState<TaskLink[]>(props.value.checklist || []);
    const [attachments, setAttachments] = useState<Attachment[]>(props.value.attachments || []);
    const [comments, setComments] = useState<Comment[]>(props.value.comments || []);
    const [completedAt, setCompletedAt] = useState(props.value.completedAt || null);

    // the names and colour ride along so the field stays labelled between picking a
    // project and the server answering with the task
    const [project, setProject] = useState<ProjectRef | null>(props.value.project ? {
        id: props.value.project,
        name: props.value.projectName || "",
        color: props.value.projectColor || "",
        goal: props.value.goal || 0,
        goalName: props.value.goalName || "",
    } : null);

    const me = useCurrentUser();
    const ws = useWorkspace();
    const isNew = !props.value.id;

    // a new task goes to the workspace's team, or the personal team in "All". Only a
    // new task's team is set here; an existing one is moved (see useMoveTask).
    const [team, setTeam] = useState(props.value.team || ws.newTeam);
    useEffect(() => {
        if (!team && ws.newTeam) setTeam(ws.newTeam);
    }, [team, ws.newTeam]);

    const move = useMoveTask({ onMoved: props.onMoved });

    const copyLink = useSnackAction(async () => {
        await navigator.clipboard.writeText(taskPermalink({
            id: props.value.id || 0,
            team: team,
            dueDate: props.value.dueDate,
        }));
    }, { success: "Link copied" });

    const archive = useSnackAction(async () => {
        await api.tasks.upsert({ ...inputRef.current, archived: true, updatedAt: version.current });
        props.onClose();
    }, { success: "Archived" });

    // a checklist item is a sub-task of another task: it doesn't break down any
    // further, and it can't repeat or be given a project of its own (see core/task)
    const isItem = props.value.type === "checklist";

    // one hint at a time, so the panel is never crowded with them. A new task skips
    // the project hint, since quick tasks often have no project.
    const flags = useUIFlags();
    const hint = isItem ? null
        : !isNew && !project && !flags.has("hint-project") ? "hint-project"
        : !!dueDate && !repeat.kind && !flags.has("hint-repeat") ? "hint-repeat"
        : null;
    const [assigned, setAssigned] = useState<number[]>(() => {
        if (!isNew) return (props.value.assignedTo || []).map(a => a.usr);
        return me ? [me.id] : [];
    });

    // the ping may not have landed when the editor mounted, so seed the default
    // assignee once it does, leaving anything the user already picked alone
    const seeded = useRef(!isNew || !!me);
    useEffect(() => {
        if (seeded.current || !me) return;

        seeded.current = true;
        setAssigned(current => current.length === 0 ? [me.id] : current);
    }, [me]);

    // the panel is re-keyed per task, so the opening tab is settled once here
    const [tab, setTab] = useState(isItem ? "blocked-by" : "checklist");

    const input: TaskInput = {
        id: props.value.id,
        type: props.value.type || "task",
        title: title,
        description: description,
        dueDate: dueDate || null,
        dueTime: dueTime || null,
        completedAt: completedAt,
        project: project ? project.id : null,
        goal: project && project.goal ? project.goal : null,
        archived: props.value.archived || false,
        repeat: repeat,
        team: team,
        assignedTo: assigned.map(usr => ({ usr: usr })),
    };

    const inputRef = useSyncedRef(input);
    const onSavedRef = useSyncedRef(props.onSaved);

    // version is the copy of the task this panel last saw, so the server can turn away
    // a save that would undo a change made somewhere else
    const version = useRef(props.value.updatedAt);

    const create = useSnackAction(async () => {
        const result = await api.tasks.upsert(inputRef.current);
        onSavedRef.current(result);
    }, { success: "Saved" });

    // completedOn moves a task that is already complete to the day it was in fact done
    const complete = useSnackAction(async (input: { completed: boolean; completedOn?: string }) => {
        const result = await api.tasks.complete({
            id: props.value.id || 0,
            completed: input.completed,
            completedOn: input.completedOn,
        });

        version.current = result.updatedAt;
        setCompletedAt(result.completedAt);
        onSavedRef.current(result);
    }, { success: "Saved" });

    // an existing task saves as it's edited, so the panel carries no save button
    const autoSave = useAutoSave({
        input: input,
        off: isNew,
        text: [title, description],
        fields: [dueDate, dueTime, repeat, assigned, project],
        save: async posted => {
            const result = await api.tasks.upsert({ ...posted, updatedAt: version.current });
            version.current = result.updatedAt;
            onSavedRef.current(result);
        },
    });

    // the last crumb is this task, so it follows the title field rather than the
    // title the stack recorded when the panel walked in
    const crumbs = props.crumbs.map((crumb, index) =>
        index === props.crumbs.length - 1 && title ? { ...crumb, title: title } : crumb);

    let status = "";
    if (autoSave.loading) status = "Saving...";
    else if (autoSave.error) status = autoSave.error;

    // a checklist item doesn't break down further, so it has no checklist tab of its own
    const tabs = [
        ...(isItem ? [] : [{
            key: "checklist", label: "Checklist", count: checklist.length,
            icon: <Checklist sx={{ fontSize: 15 }} />,
        }]),
        {
            key: "blocked-by", label: "Blocked by", count: blockedBy.length,
            icon: <Block sx={{ fontSize: 15 }} />,
        },
        {
            key: "followed-by", label: "Followed by", count: followedBy.length,
            icon: <ArrowForward sx={{ fontSize: 15 }} />,
        },
        {
            key: "gantt", label: "Gantt", count: 0,
            icon: <Timeline sx={{ fontSize: 15 }} />,
        },
    ];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0 }}>
            <Box sx={{
                display: "flex", alignItems: "center", gap: 1, flexShrink: 0,
                pl: 3, pr: 1, py: 1,
                bgcolor: "grey.50",
                borderBottom: 1, borderColor: "divider",
            }}>
                {!isNew && (
                    <TinyButton
                        variant="outlined"
                        color={completedAt ? "success" : "secondary"}
                        disabled={complete.loading}
                        startIcon={completedAt
                            ? <CheckCircle sx={{ fontSize: 16 }} />
                            : <Check sx={{ fontSize: 16 }} />}
                        onClick={() => complete.callback({ completed: !completedAt })}
                        sx={{ flexShrink: 0 }}
                    >
                        {completedAt ? "Completed" : "Mark Complete"}
                    </TinyButton>
                )}
                <TaskCrumbs value={crumbs} />
                <Typography variant="caption" noWrap
                            color={autoSave.error ? "error.main" : "text.secondary"}
                            style={{ flex: crumbs.length > 1 ? undefined : 1 }}>
                    {status}
                </Typography>

                <IconButton size="small" onClick={props.onClose}>
                    <KeyboardDoubleArrowRight fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ bgcolor: "grey.50", flex: 1, minHeight: 0, overflow: "auto" }}>
                <Box sx={{ px: 3, pb: 0, pt: 1, background: "white" }}>
                    <form id="task-panel" onSubmit={e => {
                        e.preventDefault();
                        create.callback(null);
                    }}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                            {/* a long title wraps onto more lines. It stays one line of
                                text, so enter saves a new task rather than breaking it */}
                            <TextField
                                placeholder="Task name"
                                variant="standard"
                                fullWidth
                                multiline
                                autoFocus
                                value={title}
                                onChange={e => setTitle(e.target.value.replace(/\n/g, " "))}
                                onKeyDown={e => {
                                    if (e.key !== "Enter") return;

                                    e.preventDefault();
                                    if (isNew) create.callback(null);
                                }}
                                slotProps={{ input: { disableUnderline: true, style: { fontSize: 24, fontWeight: 500 } } }}
                            />
                            {!isNew && (
                                <Typography variant="caption" color="text.secondary"
                                            sx={{ flexShrink: 0, pt: 1.25 }}>
                                    #{props.value.id}
                                </Typography>
                            )}
                            <TaskPanelMenu
                                team={team}
                                hideTeam={isItem}
                                disabled={move.loading || archive.loading}
                                onTeam={value => {
                                    if (!isNew) {
                                        move.start({ task: props.value as Task, team: value });
                                        return;
                                    }

                                    // what's picked for the old team isn't on the new one
                                    setTeam(value);
                                    setProject(null);
                                    setAssigned(me ? [me.id] : []);
                                }}
                                onCopyLink={isNew ? undefined : () => copyLink.callback(null)}
                                onArchive={isNew ? undefined : () => archive.callback(null)}
                            />
                        </Box>

                        <div style={{ height: 8 }} />
                        <FieldRow label="Due date">
                            <DueDateButton
                                hideRepeat={isItem}
                                dueDate={dueDate}
                                dueTime={dueTime}
                                repeat={repeat}
                                onChange={changes => {
                                    if (changes.dueDate !== undefined) setDueDate(changes.dueDate);
                                    if (changes.dueTime !== undefined) setDueTime(changes.dueTime);
                                    if (changes.repeat !== undefined) setRepeat(changes.repeat);
                                }}
                            />
                        </FieldRow>

                        {!!completedAt && (
                            <FieldRow label="Completed">
                                <CompletedDateButton
                                    value={completedAt}
                                    disabled={complete.loading}
                                    onChange={day => complete.callback({ completed: true, completedOn: day })}
                                />
                            </FieldRow>
                        )}

                        {!isItem && (
                            <FieldRow label="Project">
                                <TaskProject
                                    team={team}
                                    value={project ? project.id : null}
                                    goal={project ? project.goal : null}
                                    name={project ? project.name : ""}
                                    goalName={project ? project.goalName : ""}
                                    color={project ? project.color : ""}
                                    onChange={setProject}
                                    // a task that isn't saved yet has nothing to come
                                    // back to, so it stays where it is
                                    onOpen={isNew || !props.onOpenProject ? undefined : ref =>
                                        props.onOpenProject?.({ task: props.value.id || 0, ...ref })}
                                />
                            </FieldRow>
                        )}

                        {/* on a personal team the task can only be the user's */}
                        {!isPersonalTeam(ws.teams, team) && (
                            <FieldRow label="Assignee">
                                <AssigneePicker team={team} value={assigned} onChange={setAssigned} />
                            </FieldRow>
                        )}

                        {hint === "hint-project" && (
                            <Box sx={{ pt: 1 }}>
                                <Hint flag="hint-project">
                                    Put this task in a project to keep it with related work. Split a
                                    project into goals to track each part.
                                </Hint>
                            </Box>
                        )}
                        {hint === "hint-repeat" && (
                            <Box sx={{ pt: 1 }}>
                                <Hint flag="hint-repeat">
                                    A task can repeat, for example every Monday. Click the due date to
                                    set it.
                                </Hint>
                            </Box>
                        )}
                    </form>

                    {!isNew && (
                        <>
                            <div style={{ height: 8 }} />
                            {!isItem && <ChecklistProgress value={checklist} sx={{ mb: 1 }} />}
                            {/* full-bleed: the rule under the tabs runs the panel's width */}
                            <Box sx={{ mx: -3, bgcolor: "grey.50", borderTop: 1, borderColor: "divider" }}>
                                <Tabs
                                    value={tab}
                                    onChange={(_, value: string) => setTab(value)}
                                    variant="scrollable"
                                    scrollButtons={false}
                                    sx={{ px: 3, minHeight: 30, borderBottom: 1, borderColor: "divider" }}
                                >
                                    {tabs.map(item => (
                                        <Tab
                                            key={item.key}
                                            value={item.key}
                                            icon={item.icon}
                                            iconPosition="start"
                                            // the count is what says a hidden tab holds anything
                                            label={item.count > 0 ? item.label + " (" + item.count + ")" : item.label}
                                            sx={{
                                                minHeight: 30, minWidth: 0, px: 1,
                                                fontSize: 12, textTransform: "none",
                                                "& .MuiTab-icon": { mr: 0.5 },
                                            }}
                                        />
                                    ))}
                                </Tabs>
                            </Box>

                            {tab === "checklist" && (
                                <TaskChecklist
                                    taskId={props.value.id || 0}
                                    value={checklist}
                                    onChange={setChecklist}
                                    completedAt={completedAt}
                                    dueDate={dueDate || null}
                                />
                            )}

                            {tab === "blocked-by" && (
                                <TaskLinks
                                    placeholder="Wait on a task, or type a new one"
                                    taskId={props.value.id || 0}
                                    team={team}
                                    project={project ? project.id : null}
                                    direction="blocked-by"
                                    value={blockedBy}
                                    onChange={setBlockedBy}
                                    onOpen={props.onOpen}
                                />
                            )}

                            {tab === "followed-by" && (
                                <TaskLinks
                                    placeholder="Add a task that waits on this one"
                                    taskId={props.value.id || 0}
                                    team={team}
                                    project={project ? project.id : null}
                                    direction="followed-by"
                                    value={followedBy}
                                    onChange={setFollowedBy}
                                    onOpen={props.onOpen}
                                />
                            )}

                            {/* full-bleed: the chart scrolls sideways, so it gets the
                                panel's whole width to do it in */}
                            {tab === "gantt" && (
                                <Box sx={{ mx: -3 }}>
                                    <TaskGantt taskId={props.value.id || 0} onOpen={props.onOpen} />
                                </Box>
                            )}
                        </>
                    )}

                    {/* full-bleed: the rule closing the tab section runs the panel's width */}
                    <Box sx={{ mx: -3, height: 16, borderTop: isNew ? 0 : 1, borderColor: "divider" }} />

                    {/* full-bleed: cancel the panel's horizontal padding */}
                    <Box sx={{ mx: -3 }}>
                        <HtmlEditor placeholder="Add more detail to this task..."
                                    inset={panelGutter} fontSize={panelFontSize}
                                    value={description} onChange={setDescription} />
                    </Box>

                    {!isNew && (
                        <>
                            {/* full-bleed: the grey carries on out of the editor's toolbar
                                and into the section heading below, so the two read as one band */}
                            <Box sx={{ mx: -3, height: 16, bgcolor: "grey.50" }} />
                            <TaskAttachments
                                taskId={props.value.id || 0}
                                value={attachments}
                                onChange={setAttachments}
                            />

                            {/* full-bleed: the grey carries on out of the attachments section
                                and into the comments heading below, so the two read as one band */}
                            <Box sx={{ mx: -3, height: 16, bgcolor: "grey.50" }} />
                            <TaskComments
                                taskId={props.value.id || 0}
                                value={comments}
                                onChange={setComments}
                            />

                            {/* room to scroll the last comment up clear of the bottom edge */}
                            <Box sx={{ mx: -3, height: 240, bgcolor: "grey.50" }} />
                        </>
                    )}
                </Box>
            </Box>

            {move.element}

            {isNew && (
                <Box sx={{
                    display: "flex", justifyContent: "flex-end", gap: 1, p: 2,
                    bgcolor: "grey.50",
                    borderTop: 1, borderColor: "divider",
                }}>
                    <Button onClick={props.onClose}>Cancel</Button>
                    <Button type="submit" form="task-panel" variant="contained"
                            disabled={create.loading}>Save</Button>
                </Box>
            )}
        </Box>
    );
}
